import { CdkObserveContent } from '@angular/cdk/observers';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import {
  CommonMediaQueries,
  DropdownModule,
  DropdownTriggerForDirective,
  IconModule,
  MediaScreenService,
} from '@hglobal/fin-ui';
import { TranslateModule } from '@ngx-translate/core';

import { AvatarComponent, ButtonComponent } from '@iw/common';
import { UserGoogleProfile } from '@iw/core/entities';
import {
  IwDialogService,
  ProfileService,
  ThemeService,
} from '@iw/core/services';
import { environment } from '@iw/environment';

import { OrdersHistoryComponent } from '../orders-history';
import { UserMenuDialogComponent } from './components/user-menu-dialog/user-menu-dialog.component';

@Component({
  selector: 'iw-user-card-small',
  imports: [
    AvatarComponent,
    DropdownModule,
    IconModule,
    ButtonComponent,
    TranslateModule,
    CdkObserveContent,
  ],
  host: {
    '[class.opened]': 'opened()',
    '[class.desktop]': 'isDesktop()',
  },
  templateUrl: './user-card-small.component.html',
  styleUrl: './user-card-small.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserCardSmallComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialogService = inject(IwDialogService);
  readonly #profileService = inject(ProfileService);
  readonly #mediaScreenService = inject(MediaScreenService);
  readonly themeService = inject(ThemeService);

  readonly userProfile = input<UserGoogleProfile>(null);
  readonly opened = signal(false);
  readonly supportLink = environment.supportLink;

  readonly isDesktop = toSignal(
    this.#mediaScreenService.mediaMatcher(CommonMediaQueries.MD),
  );

  readonly selectUserOnMobileEffect = effect(() => {
    const isDesktop = this.isDesktop();
    const user = this.userProfile();

    if (!isDesktop && user && this.activeProfile !== 'user') {
      this.selectProfile('user');
    }
  });

  get displayAvatar() {
    const svc: any = this.#profileService as any;
    return typeof svc.displayAvatar === 'function'
      ? svc.displayAvatar(this.userProfile())
      : (this.userProfile()?.avatar ?? null);
  }

  get displayFirstLetter() {
    const svc: any = this.#profileService as any;
    if (typeof svc.displayFirstLetter === 'function') {
      return svc.displayFirstLetter(this.userProfile());
    }
    const caption = this.userProfile()?.caption ?? '';
    return caption?.charAt ? caption.charAt(0) : '';
  }

  get displayCaption() {
    const svc: any = this.#profileService as any;
    return typeof svc.displayCaption === 'function'
      ? svc.displayCaption(this.userProfile())
      : (this.userProfile()?.caption ?? '');
  }

  get activeProfile() {
    return this.#profileService.activeProfile();
  }

  get treasurerAvatar() {
    return this.#profileService.treasurerAvatar;
  }

  initDropdown(trigger: DropdownTriggerForDirective): void {
    trigger.isDropdownOpened$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(this.opened.set);
  }

  openMobileMenu(): void {
    this.#dialogService.open({
      title: '',
      components: [UserMenuDialogComponent],
      data: {
        userProfile: this.userProfile(),
      },
    });
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.themeService.setTheme(theme);
  }

  selectProfile(profile: 'user' | 'treasurer') {
    this.#profileService.setActiveProfile(profile);
  }

  openOrdersHistory(): void {
    this.#dialogService.open<OrdersHistoryComponent, void>({
      components: [OrdersHistoryComponent],
      fullWidth: true,
      fullScreen: true,
    });
  }
}
